/* eslint-disable no-param-reassign */
import axios from 'axios';
import {trace} from 'firebase/performance';

import {HttpStatusCode} from 'data/protocols/http';
import {queryClient} from 'infra/libs/react-query';
import {perf} from 'main/analytics/firebase';
import {EntryFlowEndPoints} from 'main/factories/usecases';
import {FeaturesEnum, SignInEnum} from 'presentation/constants';
import {MAX_RETRIES} from '../constants/axios-constants';
import {
	getAccessToken,
	getLoginToken,
	getLoginTokenEndpoints,
	getRefreshToken,
	removeAuthTokens,
	setAuthTokens,
	setLoginToken,
} from '../utils';
import {clearLocalStorageFeatureFlags} from '../utils/clear-local-storage-feature-flags';
import {getFeatureFlagValue} from '../utils/get-feature-flag-value';
import {RequestConfig} from './types';
import {REQUEST_CONFIG} from '../constants/request-config';
import {clearCongenereEligibilityStorage} from '../utils/clear-congenere-eligibility-storage';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';

export const axiosInstance = axios.create();

axiosInstance.interceptors.request.use(
	async (config: RequestConfig) => {
		const requestInfo = REQUEST_CONFIG;

		config.headers['Ocp-Apim-Subscription-Key'] = requestInfo.subscriptionKey;
		config.baseURL = requestInfo.baseUrl;

		const loginToken = getLoginToken();
		const accessToken = getAccessToken();
		const endpointsThatUseLoginToken = getLoginTokenEndpoints();

		if (loginToken) {
			config.headers.Authorization = `Bearer ${loginToken}`;
		}

		if (accessToken && config.url && !endpointsThatUseLoginToken.includes(config.url)) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}

		const httpMetric = trace(perf, 'httpRequest');
		config.metadata = {httpMetric};
		httpMetric.start();
		return config;
	},
	error => Promise.reject(error),
);

let retryCount = 0;

axiosInstance.interceptors.response.use(
	response => {
		const metrics = (response.config as RequestConfig).metadata;
		if (metrics) {
			metrics.httpMetric.putAttribute('status', response.status.toString());
			metrics.httpMetric.stop();
		}
		retryCount = 0;
		return response;
	},
	async error => {
		const originalRequest = error.config;

		if (error.response?.status === HttpStatusCode.unauthorized && !originalRequest.retry) {
			if (retryCount >= MAX_RETRIES) {
				removeAuthTokens();
				clearLocalStorageFeatureFlags();
				clearCongenereEligibilityStorage();
				useFacialBiometricsStore.getState().resetFacialBiometrics();
				queryClient.clear();
				window.location.href = SignInEnum.LOGIN;
				return Promise.reject(error);
			}
			originalRequest.retry = true;
			retryCount += 1;

			const refreshToken = getRefreshToken();

			if (!refreshToken) {
				removeAuthTokens();
				clearLocalStorageFeatureFlags();
				queryClient.clear();
				clearCongenereEligibilityStorage();
				useFacialBiometricsStore.getState().resetFacialBiometrics();
				window.location.href = SignInEnum.LOGIN;
				return Promise.reject(error);
			}

			await refreshAccessToken(refreshToken);
			return axiosInstance(originalRequest);
		}

		const {httpMetric} = error.config.metadata;

		if (error.response) {
			httpMetric.putAttribute('status', error.response.status.toString());
		}

		await httpMetric.stop();
		// eslint-disable-next-line no-console
		console.error('Error during error handling', error);
		return Promise.reject(error);
	},
);

async function refreshAccessToken(refreshToken: string) {
	try {
		const isV2 = await getFeatureFlagValue(FeaturesEnum.PORTAL_FEATURE_REFRESH_TOKEN);
		const url = isV2 ? EntryFlowEndPoints.refreshTokenV2 : EntryFlowEndPoints.refreshToken;
		const response = await axiosInstance.post(url, {
			refresh_token: refreshToken,
			login_token: getLoginToken(),
		});
		const newLoginToken = response.data.login_token;
		const newAccessToken = response.data.access_token;
		const newRefreshToken = response.data.refresh_token;

		setLoginToken(newLoginToken);
		setAuthTokens(newAccessToken, newRefreshToken);

		return {
			newLoginToken,
			newAccessToken,
			newRefreshToken,
		};
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Token refresh failed', error);
		throw error;
	}
}
